<template>
  <!--
    전화기 상세 — 섹션 순서는 spec 표 그대로: 연결 → 프로비저닝 → 전송 → 관리자 비밀번호 → 복구 코드 → 로그 → 반납.
    연결된 전화기뿐 아니라 미배정·반납 전화기도 연다(미배정은 [연결]·복구 코드·반납·라벨, 반납은 열람·로그만).
  -->
  <div class="pbx-dev-detail">
    <header class="pbx-dev-detail__header">
      <div class="pbx-dev-detail__heading">
        <h2 class="pbx-dev-detail__title">{{ name }}</h2>
        <div class="pbx-dev-detail__sub">
          <PbxBadge :badge="deviceStatusBadge(device.status)" />
          <span v-if="device.label" class="pbx-dev-detail__mac">{{ device.mac }}</span>
        </div>
      </div>
    </header>

    <div class="pbx-dev-detail__sections">
      <!-- 연결 -->
      <section class="pbx-dev-detail__section">
        <div class="pbx-dev-detail__section-row">
          <h3 class="pbx-dev-detail__section-title">연결</h3>
          <button
            v-if="isAssigned"
            type="button"
            class="pbx-dev-detail__ghost pbx-dev-detail__ghost--danger"
            :disabled="isPending(key('unassign'))"
            @click="unassign"
          >
            {{ isPending(key('unassign')) ? '처리 중…' : '연결 해제' }}
          </button>
          <button
            v-else-if="device.status === 'unassigned'"
            type="button"
            class="pbx-dev-detail__primary"
            @click="emit('claim', device)"
          >
            연결
          </button>
        </div>
        <dl class="pbx-dev-detail__info">
          <div>
            <dt>MAC</dt>
            <dd>
              <span class="pbx-dev-detail__mono">{{ device.mac }}</span>
              <button type="button" class="pbx-dev-detail__icon-btn" title="MAC 복사" @click="copyPbxText(device.mac)">
                <CopyOutlined />
              </button>
            </dd>
          </div>
          <div>
            <dt>모델</dt>
            <dd>{{ device.model ?? '—' }}</dd>
          </div>
          <div>
            <dt>내선 번호</dt>
            <dd>{{ extensionNumber ?? '—' }}</dd>
          </div>
          <div>
            <dt>상담사 번호</dt>
            <dd>{{ counselorNumber?.name ?? '—' }}</dd>
          </div>
          <div>
            <dt>설치 장소</dt>
            <dd>{{ site?.name ?? '—' }}</dd>
          </div>
          <div>
            <dt>라벨</dt>
            <dd v-if="!labelEditing">
              <span>{{ device.label || '—' }}</span>
              <button
                v-if="!isReturned"
                type="button"
                class="pbx-dev-detail__link"
                @click="startLabelEdit"
              >
                편집
              </button>
            </dd>
            <dd v-else class="pbx-dev-detail__label-edit">
              <input
                ref="labelInputRef"
                v-model="labelDraft"
                class="pbx-dev-detail__input"
                maxlength="64"
                placeholder="예: 원무과 1번 자리"
                @keydown.enter="onLabelEnter"
                @keydown.esc="labelEditing = false"
              />
              <button
                type="button"
                class="pbx-dev-detail__primary pbx-dev-detail__primary--sm"
                :disabled="isPending(key('label'))"
                @click="saveLabel"
              >
                저장
              </button>
              <button type="button" class="pbx-dev-detail__link" @click="labelEditing = false">취소</button>
            </dd>
          </div>
        </dl>
      </section>

      <!-- 프로비저닝 -->
      <section class="pbx-dev-detail__section">
        <div class="pbx-dev-detail__section-row">
          <h3 class="pbx-dev-detail__section-title">프로비저닝</h3>
          <span :title="checkSyncBlockedReason">
            <button
              type="button"
              class="pbx-dev-detail__ghost"
              :disabled="!!checkSyncBlockedReason || isPending(key('checkSync'))"
              @click="checkSync(device.id, false)"
            >
              {{ isPending(key('checkSync')) ? '요청 중…' : '설정 재수신' }}
            </button>
          </span>
        </div>
        <p v-if="showClaimHint" class="pbx-dev-detail__notice">
          전화기가 다음 설정 수신 때 회선을 받습니다. [설정 재수신]으로 앞당길 수 있습니다.
        </p>
        <dl class="pbx-dev-detail__info">
          <div>
            <dt>상태</dt>
            <dd><PbxBadge :badge="provisioningStateBadge(device.provisioning_state)" /></dd>
          </div>
          <div>
            <dt>마지막 수신</dt>
            <dd :title="relative(lastProvision?.at)">{{ formatPbxTime(lastProvision?.at) }}</dd>
          </div>
          <div>
            <dt>IP</dt>
            <dd>{{ lastProvision?.ip_masked ?? '—' }}</dd>
          </div>
          <div>
            <dt>UA</dt>
            <dd class="pbx-dev-detail__wrap">{{ lastProvision?.user_agent ?? '—' }}</dd>
          </div>
          <div>
            <dt>받은 파일</dt>
            <dd class="pbx-dev-detail__wrap">{{ lastProvision?.file ?? '—' }}</dd>
          </div>
          <div>
            <dt>토큰 버전</dt>
            <dd>
              <span>{{ lastProvision?.token_version ?? '—' }}</span>
              <PbxBadge v-if="oldTokenBadge" :badge="oldTokenBadge" />
            </dd>
          </div>
        </dl>
      </section>

      <!-- 전송 -->
      <section class="pbx-dev-detail__section">
        <div class="pbx-dev-detail__section-row">
          <h3 class="pbx-dev-detail__section-title">전송</h3>
          <span :title="isReturned ? RETURNED_HINT : undefined">
            <button
              type="button"
              class="pbx-dev-detail__ghost"
              :disabled="isReturned"
              @click="transportOpen = true"
            >
              전송 전환
            </button>
          </span>
        </div>
        <dl class="pbx-dev-detail__info">
          <div>
            <dt>SIP 전송</dt>
            <dd><PbxBadge :badge="transportDisplay" /></dd>
          </div>
          <div>
            <dt>SRTP 필수</dt>
            <dd>{{ srtpText }}</dd>
          </div>
        </dl>
      </section>

      <!-- 관리자 비밀번호 -->
      <section class="pbx-dev-detail__section">
        <div class="pbx-dev-detail__section-row">
          <h3 class="pbx-dev-detail__section-title">관리자 비밀번호</h3>
          <div class="pbx-dev-detail__actions">
            <!-- 반납된 전화기도 감사 열람은 된다(서버가 암호문을 유지) -->
            <button
              type="button"
              class="pbx-dev-detail__ghost"
              :disabled="isPending(key('adminRead'))"
              @click="readAdminPassword"
            >
              열람
            </button>
            <span :title="isReturned ? RETURNED_HINT : undefined">
              <button
                type="button"
                class="pbx-dev-detail__ghost"
                :disabled="isReturned || isPending(key('adminRotate'))"
                @click="rotateAdminPassword"
              >
                재발급
              </button>
            </span>
          </div>
        </div>
        <dl class="pbx-dev-detail__info">
          <div>
            <dt>마지막 재발급</dt>
            <dd :title="relative(device.admin_password_rotated_at)">
              {{ formatPbxTime(device.admin_password_rotated_at) }}
            </dd>
          </div>
        </dl>
      </section>

      <!-- 복구 코드 -->
      <section class="pbx-dev-detail__section">
        <div class="pbx-dev-detail__section-row">
          <div>
            <h3 class="pbx-dev-detail__section-title">복구 코드</h3>
            <p class="pbx-dev-detail__section-desc">
              전화기가 설정 서버 주소를 잃었을 때 현장에서 3필드만 입력해 되살리는 1회용 코드입니다.
            </p>
          </div>
          <span :title="isReturned ? RETURNED_HINT : undefined">
            <button
              type="button"
              class="pbx-dev-detail__ghost"
              :disabled="isReturned || isPending(key('recovery'))"
              @click="issueRecoveryCode(device.id, false)"
            >
              복구 코드 발급
            </button>
          </span>
        </div>
        <p class="pbx-dev-detail__plain">{{ recoveryStatusText }}</p>
      </section>

      <!-- 로그 -->
      <section class="pbx-dev-detail__section">
        <div class="pbx-dev-detail__section-row">
          <h3 class="pbx-dev-detail__section-title">로그</h3>
          <button
            type="button"
            class="pbx-dev-detail__icon-btn pbx-dev-detail__icon-btn--boxed"
            title="로그 목록 새로고침"
            :disabled="logsLoading"
            @click="loadLogs"
          >
            <ReloadOutlined />
          </button>
        </div>
        <div v-if="logsLoading && !logsLoaded" class="pbx-dev-detail__plain pbx-dev-detail__plain--muted">
          불러오는 중…
        </div>
        <div v-else-if="logsError" class="pbx-dev-detail__plain pbx-dev-detail__plain--error">
          로그 목록을 불러오지 못했습니다.
          <button type="button" class="pbx-dev-detail__link" @click="loadLogs">다시 시도</button>
        </div>
        <p v-else-if="!logs.length" class="pbx-dev-detail__plain pbx-dev-detail__plain--muted">
          전화기가 올린 로그가 없습니다. 전화기에서 MKC 1-5-9 로 로그를 올리면 여기 쌓입니다.
        </p>
        <div v-else class="pbx-dev-detail__table-wrap">
          <table class="pbx-dev-detail__table">
            <thead>
              <tr>
                <th>파일명</th>
                <th>크기</th>
                <th>업로드 시각</th>
                <th aria-label="작업"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="log in logs" :key="log.id">
                <td class="pbx-dev-detail__mono">{{ log.name }}</td>
                <td>{{ formatFileSize(log.size) }}</td>
                <td :title="relative(log.uploaded_at)">{{ formatPbxTime(log.uploaded_at) }}</td>
                <td class="pbx-dev-detail__cell-action">
                  <button
                    type="button"
                    class="pbx-dev-detail__link"
                    :disabled="isPending(key(`log:${log.id}`))"
                    @click="downloadLog(log)"
                  >
                    <DownloadOutlined /> 내려받기
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- 반납 -->
      <section class="pbx-dev-detail__section">
        <div class="pbx-dev-detail__section-row">
          <div>
            <h3 class="pbx-dev-detail__section-title pbx-dev-detail__section-title--danger">반납</h3>
            <p class="pbx-dev-detail__section-desc">
              {{ isReturned ? '이미 반납된 전화기입니다.' : '반납하면 이 전화기는 더 이상 설정을 받지 못합니다.' }}
            </p>
          </div>
          <span v-if="!isReturned" :title="isAssigned ? '먼저 연결을 해제하세요' : undefined">
            <button
              type="button"
              class="pbx-dev-detail__danger"
              :disabled="isAssigned || isPending(key('return'))"
              @click="returnDevice"
            >
              반납
            </button>
          </span>
        </div>
      </section>
    </div>

    <PbxDeviceTransportModal
      v-model:open="transportOpen"
      :inventory="inventory"
      :device="device"
      :site="site ?? null"
    />
    <PbxDeviceRevealModal v-model:reveal="reveal" />
  </div>
</template>

<script setup>
import { CopyOutlined, DownloadOutlined, ReloadOutlined } from '@ant-design/icons-vue';
import { message } from 'ant-design-vue';
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';

import { getCallApiErrorCode } from '@/views/Callbot/service/callApiError';
import { copyPbxText, usePbxAction } from '@/views/Callbot/service/usePbxAction';
import {
  checkSyncOutcome,
  deviceStatusBadge,
  formatFileSize,
  formatPbxRelative,
  formatPbxTime,
  provisioningStateBadge,
  transportBadge,
} from '@/views/Callbot/service/pbxExtensions';

import PbxBadge from './PbxBadge.vue';
import PbxDeviceRevealModal from './PbxDeviceRevealModal.vue';
import PbxDeviceTransportModal from './PbxDeviceTransportModal.vue';
import { confirmPbx, deviceName, isImeEnter } from './PbxDeviceShared';

const props = defineProps({
  inventory: { type: Object, required: true },
  /** PbxDeviceItem */
  device: { type: Object, required: true },
  /** 방금 [연결]한 전화기 — 프로비저닝 섹션에 첫 설정 수신 안내를 단다 */
  showClaimHint: { type: Boolean, default: false },
});
const emit = defineEmits(['claim', 'returned']);

const RETURNED_HINT = '반납된 전화기입니다';
const DEVICE_RELOAD = ['devices', 'extensions', 'counselorNumbers'];

const { run, isPending } = usePbxAction(props.inventory);

// 조작 키는 전화기별 — 다른 전화기를 연 동안 앞 전화기 조작이 끝나도 버튼이 엉키지 않는다
// 기다림을 낀 조작은 클릭 때 뜬 id 를 넘긴다
const key = (action, id = props.device.id) => `device:${action}:${id}`;

const name = computed(() => deviceName(props.device));
const isAssigned = computed(() => props.device.status === 'assigned');
const isReturned = computed(() => props.device.status === 'returned');
const lastProvision = computed(() => props.device.last_provision);

const site = computed(() => props.inventory.siteById.value.get(props.device.site_id));
const extension = computed(() =>
  props.device.extension_id ? props.inventory.extensionById.value.get(props.device.extension_id) : undefined
);
const counselorNumber = computed(() =>
  props.device.counselor_number_id
    ? props.inventory.counselorNumberById.value.get(props.device.counselor_number_id)
    : undefined
);
// 내선 목록에 아직 없으면(재조회 전) 상담사 번호 요약의 번호로
const extensionNumber = computed(
  () => extension.value?.number ?? counselorNumber.value?.pbx_extension?.number ?? null
);

function relative(ms) {
  return ms == null ? undefined : formatPbxRelative(ms, props.inventory.now.value);
}

// --- 프로비저닝 --------------------------------------------------------------

const oldTokenBadge = computed(() => {
  const v = lastProvision.value?.token_version;
  const s = site.value;
  if (v == null || !s || v === s.token_version) return null;
  return {
    label: '구 토큰',
    tone: 'warn',
    title: `설치 장소 현재 토큰 버전은 ${s.token_version} 입니다. 전화기가 아직 새 프로비저닝 URL 로 설정을 받지 않았습니다.`,
  };
});

const checkSyncBlockedReason = computed(() => {
  if (isReturned.value) return RETURNED_HINT;
  if (!isAssigned.value) return '내선에 연결된 전화기만 설정을 다시 보낼 수 있습니다';
  return undefined;
});

/**
 * 전화기 id 는 클릭 때 한 번 떠서 재귀까지 넘긴다 — 응답·확인을 기다리는 사이 다른 전화기를
 * 열었으면 그 결과는 버린다(강제 전송 확인을 엉뚱한 전화기에 묻지 않게).
 */
async function checkSync(id, force) {
  const res = await run(key('checkSync', id), () => props.inventory.api.checkSyncPbxDevice(id, force ? { force: true } : {}), {
    // 분기는 reason 으로 — 토스트는 아래에서 직접
    success: null,
    fallback: '설정 재수신을 요청하지 못했습니다.',
  });
  if (!res || props.device.id !== id) return;
  const outcome = checkSyncOutcome(res);
  if (outcome.kind === 'info') {
    message.info(outcome.text);
    return;
  }
  if (force) return;
  const ok = await confirmPbx({ title: '설정 재수신', content: outcome.text, okText: '강제 전송', danger: true });
  if (ok && props.device.id === id) await checkSync(id, true);
}

// --- 전송 --------------------------------------------------------------------

const transportOpen = ref(false);

const transportDisplay = computed(() =>
  transportBadge(
    props.device.sip_transport,
    props.device.sip_port,
    site.value ? { transport: site.value.sip_transport_default, port: site.value.sip_port_default } : null
  )
);

const srtpText = computed(() => {
  const v = props.device.srtp_require;
  if (v === null) {
    if (!site.value) return '설치 장소 기본값';
    return `설치 장소 기본값(${site.value.srtp_require ? '필수' : '해제'})`;
  }
  return v ? '필수' : '해제';
});

// --- 연결 / 라벨 / 반납 ------------------------------------------------------

async function unassign() {
  const d = props.device;
  const ok = await confirmPbx({
    title: '연결 해제',
    content: `전화기 ${deviceName(d)} 에서 내선 ${extensionNumber.value ?? '—'} 회선을 뗍니다. 전화기는 다음 설정 수신 때 회선이 사라지고, 내선의 SIP 비밀번호는 회전됩니다(떼어낸 전화기가 계속 등록하지 못하게). 상담사 번호는 그대로 남습니다. 계속할까요?`,
    okText: '연결 해제',
    danger: true,
  });
  if (!ok) return;
  await run(key('unassign'), () => props.inventory.api.unassignPbxDevice(d.id), {
    success: '연결 해제를 완료했습니다.',
    reload: [...DEVICE_RELOAD],
    fallback: '연결을 해제하지 못했습니다.',
  });
}

const labelEditing = ref(false);
const labelDraft = ref('');
const labelInputRef = ref(null);

function startLabelEdit() {
  labelDraft.value = props.device.label ?? '';
  labelEditing.value = true;
  nextTick(() => labelInputRef.value?.focus());
}

function onLabelEnter(e) {
  if (isImeEnter(e)) return;
  e.preventDefault();
  saveLabel();
}

async function saveLabel() {
  const next = labelDraft.value.trim();
  if (next === (props.device.label ?? '')) {
    labelEditing.value = false;
    return;
  }
  const res = await run(
    key('label'),
    // 비우면 null — 라벨 제거(목록은 MAC 으로 보인다)
    () => props.inventory.api.updatePbxDevice(props.device.id, { label: next || null }),
    { success: '라벨 변경을 완료했습니다.', reload: ['devices'], fallback: '라벨을 바꾸지 못했습니다.' }
  );
  if (res) labelEditing.value = false;
}

async function returnDevice() {
  const d = props.device;
  const ok = await confirmPbx({
    title: '반납',
    content: `전화기 ${deviceName(d)} 를 반납 처리합니다. 이후 이 전화기는 설정을 받지 못합니다. 계속할까요?`,
    okText: '반납',
    danger: true,
  });
  if (!ok) return;
  // 204(본문 없음) — resolve 되면 성공. 설치 장소 전화기 수가 바뀌므로 설치 장소도 다시 받는다
  const done = await run(key('return'), () => props.inventory.api.deletePbxDevice(d.id).then(() => true), {
    success: '반납을 완료했습니다.',
    reload: [...DEVICE_RELOAD, 'sites'],
    fallback: '반납하지 못했습니다.',
  });
  if (done) emit('returned', d.id);
}

// --- 관리자 비밀번호 / 복구 코드 (1회 표시 값) --------------------------------

/** PbxDeviceReveal | null — 결과 모달이 닫히면(after-close) null 로 돌아온다 */
const reveal = ref(null);

async function readAdminPassword() {
  const id = props.device.id;
  const ok = await confirmPbx({
    title: '관리자 비밀번호 열람',
    content: '열람 기록이 감사 로그에 남습니다. 계속할까요?',
    okText: '열람',
  });
  // 확인·응답을 기다리는 사이 다른 전화기를 열었으면 열람하지도, 보이지도 않는다
  if (!ok || props.device.id !== id) return;
  const res = await run(key('adminRead', id), () => props.inventory.api.getPbxDeviceAdminPassword(id), {
    success: null,
    fallback: '관리자 비밀번호를 불러오지 못했습니다.',
  });
  if (res && props.device.id === id) reveal.value = { kind: 'adminPassword', value: res.admin_password };
}

async function rotateAdminPassword() {
  const id = props.device.id;
  const ok = await confirmPbx({
    title: '관리자 비밀번호 재발급',
    content:
      '전화기 관리자 비밀번호를 새로 만듭니다. 새 값은 전화기가 설정을 다시 받은 뒤 적용되며 [열람]으로 확인합니다. 계속할까요?',
    okText: '재발급',
  });
  if (!ok) return;
  await run(key('adminRotate'), () => props.inventory.api.rotatePbxDeviceAdminPassword(id), {
    success: '관리자 비밀번호 재발급을 완료했습니다.',
    reload: ['devices'],
    fallback: '관리자 비밀번호를 재발급하지 못했습니다.',
  });
}

const recoveryStatusText = computed(() => {
  const rc = props.device.recovery_code;
  if (!rc) return '발급 안 됨';
  if (rc.used_at != null) return `사용됨 ${formatPbxTime(rc.used_at)}`;
  if (rc.expires_at > props.inventory.now.value) return `유효 ${formatPbxTime(rc.expires_at)} 까지`;
  return `만료됨 ${formatPbxTime(rc.expires_at)}`;
});

/** checkSync 와 같이 id 를 클릭 때 떠서 넘기고, 선택이 바뀌었으면 결과·재발급 확인을 버린다 */
async function issueRecoveryCode(id, replace) {
  // onError 콜백에서 채운다 — PBX_RECOVERY_CODE_ACTIVE 면 오류 토스트 대신 재발급 확인으로 잇는다
  const after = { confirmReplace: false };
  const res = await run(
    key('recovery', id),
    () => props.inventory.api.createPbxDeviceRecoveryCode(id, replace ? { replace: true } : {}),
    {
      // 결과 모달이 성공 표시다
      success: null,
      reload: ['devices'],
      fallback: '복구 코드를 발급하지 못했습니다.',
      onError: (err) => {
        if (!replace && getCallApiErrorCode(err) === 'PBX_RECOVERY_CODE_ACTIVE') {
          after.confirmReplace = true;
          return true;
        }
        return false;
      },
    }
  );
  if (props.device.id !== id) return;
  if (res) {
    reveal.value = { kind: 'recoveryCode', code: res.code, expires_at: res.expires_at, recovery_url: res.recovery_url };
    return;
  }
  if (!after.confirmReplace) return;
  const ok = await confirmPbx({
    title: '복구 코드 재발급',
    content: '이미 유효한 복구 코드가 있습니다. 새로 발급하면 기존 코드는 무효가 됩니다. 새로 발급할까요?',
    okText: '새로 발급',
    danger: true,
  });
  if (ok && props.device.id === id) await issueRecoveryCode(id, true);
}

// --- 로그 --------------------------------------------------------------------

/** PbxDeviceLogItem[] */
const logs = ref([]);
const logsLoading = ref(false);
const logsLoaded = ref(false);
const logsError = ref(false);
let logsSeq = 0;

async function loadLogs() {
  const id = props.device.id;
  const my = ++logsSeq;
  logsLoading.value = true;
  logsError.value = false;
  try {
    const res = await props.inventory.api.listPbxDeviceLogs(id);
    if (my !== logsSeq) return;
    logs.value = res.items ?? [];
    logsLoaded.value = true;
  } catch {
    if (my !== logsSeq) return;
    logsError.value = true;
  } finally {
    if (my === logsSeq) logsLoading.value = false;
  }
}

async function downloadLog(log) {
  const id = props.device.id;
  // 실패 응답이 Blob 으로 와도 api.js 가 JSON 봉투로 되돌려 두므로 오류 문구는 그대로 파싱된다
  const blob = await run(key(`log:${log.id}`), () => props.inventory.api.downloadPbxDeviceLog(id, log.id), {
    success: null,
    fallback: '로그를 내려받지 못했습니다.',
  });
  if (!blob) return;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = log.name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // 클릭이 다운로드를 시작한 뒤에 해제 — 바로 해제하면 일부 브라우저가 빈 파일을 받는다
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// 다른 전화기를 열면 편집·로그를 새로 — 로그는 상세를 열 때만 받는다(목록에는 없다)
watch(
  () => props.device.id,
  () => {
    labelEditing.value = false;
    transportOpen.value = false;
    // 1회 표시 값은 연 전화기의 것만 — 다른 전화기로 넘어가면 닫는다
    reveal.value = null;
    logs.value = [];
    logsLoaded.value = false;
    loadLogs();
  },
  { immediate: true }
);

// admin 라우트는 KeepAlive 가 아니다 — 상세가 내려갈 때(병원 변경·탭 이동·라우트 이탈) 1회 표시 값을
// 즉시 비워, 감사 기록 없이 다시 보이지 않게 한다
onBeforeUnmount(() => {
  reveal.value = null;
});
</script>

<style scoped lang="scss">
.pbx-dev-detail {
  display: flex;
  flex-direction: column;
  gap: 20px;
  font-size: 13px;

  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
  }

  &__heading {
    min-width: 0;
  }

  &__title {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    color: #262626;
    word-break: break-all;
  }

  &__sub {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 6px;
  }

  &__mac {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12px;
    color: #8c8c8c;
  }

  &__sections {
    display: flex;
    flex-direction: column;
    gap: 20px;
    max-width: 720px;
  }

  &__section {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding-bottom: 20px;
    border-bottom: 1px solid #fafafa;

    &:last-child {
      border-bottom: none;
      padding-bottom: 0;
    }
  }

  &__section-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
  }

  &__section-title {
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    color: #262626;

    &--danger {
      color: #cf1322;
    }
  }

  &__section-desc {
    margin: 2px 0 0;
    font-size: 12px;
    color: #8c8c8c;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__info {
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;

    > div {
      display: flex;
      gap: 12px;
    }

    dt {
      flex: none;
      width: 110px;
      color: #8c8c8c;
    }

    dd {
      margin: 0;
      min-width: 0;
      display: flex;
      align-items: center;
      gap: 8px;
      color: #262626;
    }
  }

  &__wrap {
    word-break: break-all;
  }

  &__mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  }

  &__notice {
    margin: 0;
    padding: 8px 12px;
    border: 1px solid #91caff;
    border-radius: 8px;
    background: #e6f4ff;
    color: #0958d9;
    line-height: 1.5;
  }

  &__plain {
    margin: 0;
    color: #262626;

    &--muted {
      color: #8c8c8c;
    }

    &--error {
      color: #cf1322;
    }
  }

  &__label-edit {
    flex: 1;
  }

  &__input {
    flex: 1;
    min-width: 0;
    max-width: 280px;
    padding: 5px 10px;
    border: 1px solid #d9d9d9;
    border-radius: 8px;
    font-size: 13px;

    &:focus {
      outline: none;
      border-color: #1668dc;
    }
  }

  &__table-wrap {
    overflow-x: auto;
    border: 1px solid #f0f0f0;
    border-radius: 8px;
  }

  &__table {
    width: 100%;
    border-collapse: collapse;

    th {
      padding: 8px 12px;
      background: #fafafa;
      border-bottom: 1px solid #f0f0f0;
      color: #8c8c8c;
      font-size: 12px;
      font-weight: 600;
      text-align: left;
      white-space: nowrap;
    }

    td {
      padding: 8px 12px;
      border-bottom: 1px solid #fafafa;
      color: #262626;
      white-space: nowrap;
    }

    tr:last-child td {
      border-bottom: none;
    }
  }

  &__cell-action {
    text-align: right;
  }

  &__primary {
    flex: none;
    padding: 6px 16px;
    border: 1px solid #1668dc;
    border-radius: 8px;
    background: #1668dc;
    color: #fff;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;

    &:hover:not(:disabled) {
      background: #0f5bc4;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    &--sm {
      padding: 4px 12px;
    }
  }

  &__ghost {
    flex: none;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    background: #fff;
    color: #595959;
    font-size: 13px;
    cursor: pointer;

    &:hover:not(:disabled) {
      border-color: #bfbfbf;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &--danger:hover:not(:disabled) {
      border-color: #cf1322;
      color: #cf1322;
    }
  }

  &__danger {
    flex: none;
    padding: 6px 16px;
    border: 1px solid #ff4d4f;
    border-radius: 8px;
    background: #fff;
    color: #cf1322;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;

    &:hover:not(:disabled) {
      background: #fff2f0;
    }

    &:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }
  }

  &__link {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 0;
    border: none;
    background: none;
    color: #1668dc;
    font-size: 13px;
    cursor: pointer;

    &:hover:not(:disabled) {
      text-decoration: underline;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &__icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 2px 4px;
    border: none;
    background: none;
    color: #8c8c8c;
    cursor: pointer;

    &:hover:not(:disabled) {
      color: #1668dc;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &--boxed {
      width: 28px;
      height: 28px;
      border: 1px solid #e8e8e8;
      border-radius: 8px;
      background: #fff;
    }
  }
}
</style>
